import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

export function PlansComparison() {
  return (
    <div className="relative overflow-hidden py-20">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-0 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -right-1/4 bottom-0 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
      </div>

      <div className="relative text-center">
        <div className="px-4 sm:px-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="m-0"
          >
            <span className="relative inline-block">
              <span className="absolute -inset-1 rotate-1 -skew-y-3 rounded-tl-xl rounded-br-xl bg-gradient-to-r from-primary/10 to-primary/5"></span>
              <span className="relative text-4xl font-light text-foreground">
                Plans vs. credit packs. Which option is right for you?
              </span>
            </span>
          </motion.h2>
        </div>
      </div>

      <div className="relative mt-12 px-4 pb-20 sm:px-20">
        <div className="mx-auto max-w-7xl">
          <div className="container mx-auto">
            <div className="mx-4 px-4 sm:mx-6 sm:px-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-card/50 border-border/50 rounded-xl border p-6 shadow-lg backdrop-blur-sm"
              >
                <div className="flex flex-col space-y-8">
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <div className="sm:w-1/3">
                      <h3 className="m-0">
                        <p className="mr-2 flex items-center gap-2 pr-4 text-2xl font-semibold text-foreground sm:pr-20">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                          >
                            <path d="M4 9a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3z" />
                            <path d="M8 7V5c0-1.1.9-2 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M12 12v.01" />
                            <path d="M16 12v.01" />
                            <path d="M8 12v.01" />
                          </svg>
                          Subscription
                        </p>
                      </h3>
                    </div>
                    <div className="border-border flex-1 border-b-2 pt-4 pb-2 pl-2">
                      <ul className="text-muted-foreground space-y-3">
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          Best for your typical image generation needs
                        </li>
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          Priced for value — most popular option
                        </li>
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          All plans allow you to use all AI features including
                          custom model training
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                    <div className="sm:w-1/3">
                      <h3 className="m-0">
                        <p className="flex items-center gap-2 text-2xl font-semibold text-foreground">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-5 w-5"
                          >
                            <circle cx="12" cy="12" r="8" />
                            <path d="M14.5 9a2 2 0 0 0-2-2h-1a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4h-1a2 2 0 0 1-2-2" />
                            <path d="M12 6v2m0 8v2" />
                          </svg>{' '}
                          Credit packs
                        </p>
                      </h3>
                    </div>
                    <div className="flex-1 p-4">
                      <ul className="text-muted-foreground space-y-3">
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          One time purchases let you pay as you go
                        </li>
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          Can be used for any AI generation on the site
                        </li>
                        <li className="flex items-center gap-2 p-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                          Best for inconsistent AI generation needs
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative text-center">
        <div className="p-8 sm:p-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="m-0"
          >
            <span className="text-2xl font-bold text-foreground">
              Still have questions?
            </span>
          </motion.h3>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="pb-16"
          >
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <p className="m-0">
                  <span className="text-lg">
                    <a
                      href="#plans-faq"
                      className="group inline-flex items-center gap-2 text-primary hover:underline"
                    >
                      Check out our FAQ
                      <span className="duration-200 transition-transform group-hover:translate-y-1">
                        ↓
                      </span>
                    </a>
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
